<script>
  import { inform, rest, shownModal, t } from '@dabble/app';
  import Modal from '@dabble/toolkit/Modal.svelte';
  import dabbleUInverse from '../images/dabble-u-inverse.svg';
  import dabbleURgular from '../images/dabble-u.svg';
  import { challengeStore, nanowrimo } from '../nanowrimo';

  import Icon from '@dabble/toolkit/Icon.svelte';
  import { mdiCancel, mdiCheckCircleOutline, mdiLoading, mdiOpenInNew } from '@mdi/js';

  import { plugins } from '@dabble/app';
  import { isCamp, monthAfter } from '../nanowrimo';

  const { uiTheme } = plugins.stores;
  $: dabbleU = $uiTheme === 'dark' ? dabbleUInverse : dabbleURgular;

  $: checkingCircle = false;
  $: subscription = $plugins.subscription;
  $: status = getSubscriptionStatus(subscription);
  $: canHaveTrial = !$isCamp && status !== 'active';
  // An extended trial will finish Dec 2nd, so trial_end will be greater than Dec 1st, no matter the timezone
  $: hasExtendedTrial = (subscription?.trial_end || 0) * 1000 > $monthAfter && status === 'trialing';
  $: canStartOrExtendTrial = canHaveTrial && (status === 'canceled' || !hasExtendedTrial);

  $: isMember = $challengeStore?.isMember;
  $: isChallengeOpen = !$isCamp;
  $: hasJoinedChallenge = $challengeStore?.participating;
  $: canJoin = isMember;
  $: alreadyJoined = hasJoinedChallenge;
  $: if ($shownModal === 'nanowrimoInvite') checkCircle();

  async function joinChallenge() {
    if (canStartOrExtendTrial) plugins.stores.billing.startNaNoWriMoTrial();

    if (isMember && !hasJoinedChallenge && isChallengeOpen) {
      challengeStore.update({ participating: true });
      hasJoinedChallenge = true;
    }

    $shownModal = 'challengeJoined';
  }

  function getSubscriptionStatus(subscription) {
    if (!subscription) return 'canceled';
    if (subscription.status === 'trialing') return 'trialing';
    return 'active';
  }

  function closeModal() {
    $shownModal = null;
  }

  async function decline() {
    closeModal();
    challengeStore.update({ participating: false });
    nanowrimo.update({ participating: false });
    inform('info', $t('nanowrimo_maybe_later'), $t('nanowrimo_turn_on_later'));
  }

  async function checkCircle() {
    if ($challengeStore.isMember || !isChallengeOpen) return;
    checkingCircle = true;
    try {
      const { isMember } = await rest.GET('/circle/check-60day');
      challengeStore.update({ isMember, modified: Date.now() });
    } catch (e) {}
    checkingCircle = false;
  }

  function getActionText(canStartOrExtendTrial, alreadyJoined, status) {
    if (canStartOrExtendTrial) {
      return status === 'canceled' ? 'challenge_trial_start' : 'challenge_trial_extend';
    }
    if (alreadyJoined) {
      return 'challenge_goto_settings';
    }
    return 'challenge_join';
  }
</script>

{#if $shownModal === 'nanowrimoInvite'}
  <Modal title={$t('nanowrimo_title')} size="medium" noClose>
    <div class="logo">
      <img src={dabbleU} alt="Dabble U" />
    </div>        
    <div class="lead">{@html isChallengeOpen ? $t('nanowrimo_lead') : $t('nanowrimo_lead_no_challenge')}</div>
    <section class="nano-invite-main"> 
      {#if isChallengeOpen}
      <div>
        <ul>
          {@html $t('dabbleu')}
          {#if canHaveTrial}
            {@html $t('free_trial')}
          {/if}
        </ul>
      </div>
      {/if}
      
    </section>
    <div class="nano-invite-main">
      {#if isChallengeOpen}
      <div class="adjust">
        {#if checkingCircle}
          <Icon path={mdiLoading} spin />
        {:else if isMember}
          <div class="joined">
            <Icon path={mdiCheckCircleOutline} /> <strong>{$t('dabbleu_joined')}</strong>
          </div>
          {#if hasJoinedChallenge}
            <div class="joined">
              <Icon path={mdiCheckCircleOutline} /> <strong>{$t('challenge_participation')}</strong>
            </div>
          {:else}
            <div class="joined not">
              <Icon path={mdiCancel} /> <strong>{$t('challenge_participation')}</strong>
            </div>
          {/if}
        {:else}
          <a
            href="https://www.dabbleu.com/sign_up?post_login_redirect=https%3A%2F%2Fwww.dabbleu.com%2Fc%2Fjoin-start-here%2F&request_host=www.dabbleu.com&user%5Binvitation_token%5D=#email"
            target="_blank"
            class="btn primary">{$t('dabbleu_join')} <Icon path={mdiOpenInNew} inline /></a
          >
        {/if}
      </div>
      {/if}
      
    </div>
    <footer>
      <button
        class="btn primary"
        class:outline={!canStartOrExtendTrial && alreadyJoined}
        on:click={joinChallenge}
        disabled={!canJoin}
      >
        {$t(getActionText(canStartOrExtendTrial, alreadyJoined, status))}
      </button>
      {#if alreadyJoined}
        <button class="btn" on:click={closeModal}>{$t('close')}</button>
      {:else}
        <button class="btn" on:click={decline}>{$t('nanowrimo_not_interested')}</button>
      {/if}
    </footer>
  </Modal>
{/if}

<style scoped>
  .logo {
    text-align: center;
  }

  .nano-invite-main {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-around;
    gap: 2rem;
    padding-bottom: 2rem;
  }

  .nano-invite-main ul {
    margin: 0;
  }

  :global(.noSync) {
    color: var(--text-color-lightest);
    text-decoration: line-through;
  }

  .nano-invite-main > div {
    width: 400px;
  }

  .nano-invite-main .adjust {
    padding-left: 2.5rem;
  }

  .nano-invite-main .adjust2 {
    padding-left: 1.5rem;
  }

  .joined {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }
  .joined :global(svg) {
    color: var(--brand-primary);
    font-size: 1.5rem;
  }

  .joined.not {
    color: var(--text-color-lightest);
  }
  .joined.not :global(svg) {
    color: var(--brand-danger);
    font-size: 1.5rem;
  }
  @media (min-width: 992px) {
    .nano-invite-main {
      flex-direction: row;
      align-items: flex-start;
    }
    .nano-invite-main > div {
      width: 100%;
    }
  }
</style>
